import axios from "axios";
import { useEffect, useState } from "react";
import { Input } from "../input";
import { Trash } from "lucide-react";
import { ArticleModal } from "../../../utilities/blogutils";
import Swal from "sweetalert2";
import { useRef } from "react";

type BlogsListProps = {
    tab: string,
    setTab: (tab: string) => void,
    baseUrl: string,
    currentUser: any,
    client: any,
}
function useDebounce<T>(value: T, delay = 400): T {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
}
export default function BlogsList({ tab, setTab, baseUrl, currentUser, client }: BlogsListProps) {
    const [blogs, setBlogs] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(false);
    const [openPost, setOpenPost] = useState(null);

    const [paging, setPaging] = useState(0);
    const [totalBlogs, setTotalBlogs] = useState(0);

    const CUSTOMERS_PER_PAGE = 10;
    const PAGE_BUTTONS_TO_SHOW = 5;

    const debouncedSearch = useDebounce(search, 400);
    const debouncedStatus = useDebounce(statusFilter, 300);
    const abortRef = useRef<AbortController | null>(null);

    const getCustomers = async (page: number) => {
        try {
            // Abort previous request
            if (abortRef.current) {
                abortRef.current.abort();
            }

            const controller = new AbortController();
            abortRef.current = controller;

            setLoading(true);

            const response = await axios.get(baseUrl + "/getBlogs", {
                params: {
                    token: currentUser?.token,
                    limit: page === 0 ? 0 : page * CUSTOMERS_PER_PAGE,
                    length: CUSTOMERS_PER_PAGE,
                    client_uid: client?.uid,
                    search: debouncedSearch,
                    status: debouncedStatus,
                },
                signal: controller.signal,
            });

            if (response.data.status === "OK") {
                setBlogs(response.data.data || []);
                setTotalBlogs(response.data.total || 0);
            }
        } catch (err: any) {
            if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
                // Request was aborted → ignore
                return;
            }
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!currentUser?.token || !client?.uid) return;
        getCustomers(paging);

        return () => {
            abortRef.current?.abort();
        };
    }, [
        paging,
        currentUser?.token,
        client?.uid,
        debouncedSearch,
        debouncedStatus
    ]);

    useEffect(() => {
        setPaging(0);
    }, [search, statusFilter]);

    const totalPages = Math.ceil(totalBlogs / CUSTOMERS_PER_PAGE);

    const goToPage = (page: number) => {
        if (page < 0 || page >= totalPages) return;
        setPaging(page);
    };

    // Calculate visible page buttons dynamically
    const getVisiblePages = () => {
        let start = Math.max(paging - Math.floor(PAGE_BUTTONS_TO_SHOW / 2), 0);
        let end = start + PAGE_BUTTONS_TO_SHOW;

        if (end > totalPages) {
            end = totalPages;
            start = Math.max(end - PAGE_BUTTONS_TO_SHOW, 0);
        }

        const pages = [];
        for (let i = start; i < end; i++) {
            pages.push(i);
        }
        return pages;
    };
    const handleDeleteClick = async (blog_id: string) => {

        // ✅ Await the Swal.fire() promise
        const result = await Swal.fire({
            title: "Slett bloggen",
            html: `
                <h3 style="margin-bottom: 2rem !important">Er du sikker på at du vil slette dette blogginnlegget?</h3>
                <div style="text-align: left; margin: 0 0 16px; padding: 12px 14px; border-radius: 12px; border: 1px solid #e5e7eb; background: #f9fafb; font-size: 14px; color: #111827;">
                    Når du slette:<br>
                    • Blogginnlegget vil bli slettet permanent.<br>
                    • <strong>Du kan ikke angre denne slettingen</strong>.
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Slett bloggen",
            cancelButtonText: "Close",
            customClass: {
                confirmButton: "swal-confirm-btn",
                cancelButton: "swal-cancel-btn",
            },
        });
        // ✅ Now result.isConfirmed is available
        if (result.isConfirmed) {
            try {
                const formData = new FormData();
                formData.append("id", blog_id);
                formData.append("token", currentUser.token);

                const response = await axios.post(baseUrl + "/deleteBlog", formData);

                if (response.data.status === "OK") {
                    Swal.fire({
                        icon: "success",
                        title: "Blogg slettet",
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    setPaging(0);
                    getCustomers(paging);
                } else {
                    Swal.fire({
                        icon: "error",
                        title: "Feil",
                        text: response.data.message || "Noe gikk galt.",
                    });
                }
            } catch (error) {
                console.error("Error deleteing blog:", error);
                Swal.fire({
                    icon: "error",
                    title: "Feil",
                    text: "Kunne ikke avbestille avtalen. Prøv igjen senere.",
                });
            }
        }


    }
    return (
        <div className="min-h-screen bg-slate-50 p-6 md:p-8">
            <div className="grid grid-cols-2 gap-6 mb-3">
                <div>
                    <h1 className="text-2xl font-semibold">Bloggen list</h1>
                </div>
                <div className="text-end">
                    <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-2xl px-5" onClick={() => setTab("Blog detail")}>Create</button>
                </div>
            </div>
            {/* MAIN LAYOUT */}
            <div className="grid grid-cols-1 gap-6 items-start">
                {/* LEFT: CUSTOMER LIST */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-slate-100">
                        <Input
                            type="text"
                            placeholder="Søk på title, status"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full px-3 py-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                        />

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-slate-50"
                        >
                            <option value="all">Alle statuser</option>
                            <option value="Scheduled">Scheduled</option>
                            <option value="Published">Published</option>
                            <option value="Draft">Draft</option>
                        </select>
                    </div>

                    <div className="overflow-x-auto md:overflow-visible">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs text-slate-500 bg-slate-50">
                                    <th className="text-left px-4 py-2">Title</th>
                                    <th className="text-left px-4 py-2">Type</th>
                                    <th className="text-left px-4 py-2">Date time</th>
                                    <th className="text-left px-4 py-2">Read time</th>
                                    <th className="text-left px-4 py-2">Status</th>
                                    <th className="text-center px-4 py-2">Handlinger</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6}>
                                            <div className="flex items-center justify-center h-64">
                                                <div className="flex flex-col items-center">
                                                    <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                                                    <p className="mt-3 text-gray-600 animate-pulse">Laster...</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {blogs.map((c) => (
                                            <tr
                                                key={c.id}
                                                className={`border-t border-slate-100 hover:bg-indigo-50/40 transition ${selectedId === c.id ? "bg-indigo-50/60" : "bg-white"
                                                    }`}
                                            >
                                                <td onClick={() => setOpenPost(c)} className="px-4 py-3 cursor-pointer">
                                                    <div
                                                        className="font-medium text-slate-900"
                                                    >
                                                        {c.name}
                                                    </div>
                                                    <div className="text-xs text-slate-500">{c.title}</div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">{c.template}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">{c.formatted_created_at}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">{c.readTimeMin}</td>
                                                <td className="px-4 py-3">
                                                    {c.status}
                                                </td>
                                                <td className="text-center">
                                                    <button onClick={() => handleDeleteClick(c.id)} className="text-red-600 text-lg"><Trash /></button>
                                                </td>
                                            </tr>
                                        ))}
                                    </>
                                )}
                                {blogs.length === 0 && !loading && (
                                    <tr>
                                        <td className="px-4 py-6 text-center text-sm text-slate-500" colSpan={6}>
                                            Ingen blogs matcher søket.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION */}
                    {totalPages > 1 && (
                        <div className="flex justify-center mt-4 gap-2 px-4 pt-4 pb-0">
                            <button
                                onClick={() => goToPage(paging - 1)}
                                disabled={paging === 0}
                                className="px-3 py-1 rounded border border-slate-300 bg-white disabled:opacity-50"
                            >
                                Prev
                            </button>

                            {getVisiblePages().map((i) => (
                                <button
                                    key={i}
                                    onClick={() => goToPage(i)}
                                    className={`px-3 py-1 rounded border border-slate-300 ${paging === i ? "bg-indigo-500 text-white" : "bg-white"
                                        }`}
                                >
                                    {i + 1}
                                </button>
                            ))}

                            <button
                                onClick={() => goToPage(paging + 1)}
                                disabled={paging === totalPages - 1}
                                className="px-3 py-1 rounded border border-slate-300 bg-white disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}

                    {totalBlogs > 0 && (
                        <div className="flex justify-center gap-2 px-4">
                            <div>
                                <small className="text-gray-500"><em>Total no of records:</em> <b>{totalBlogs}</b></small>
                            </div>
                        </div>
                    )}

                    {openPost ? <ArticleModal post={openPost} onClose={() => setOpenPost(null)} /> : null}
                </div>
            </div>
        </div>
    )
}